import { useState } from "react";
import { Link } from "react-router-dom";
import { AppLayout } from "@/design-system/layout/AppLayout";
import { Search, Plus, Copy, AlertCircle } from "lucide-react";

type TournamentStatus = "draft" | "published" | "registrationOpen" | "inProgress" | "completed";

interface Tournament {
  id: number;
  name: string;
  status: TournamentStatus;
  dateRange: string;
  venue: string;
  registeredTeams: number;
  totalTeams: number;
  revenue: number;
  isArchived: boolean;
}

const allTournaments: Tournament[] = [
  {
    id: 1,
    name: "KC Youth Basketball Classic",
    status: "published",
    dateRange: "May 15-16, 2024",
    venue: "Kansas City, MO",
    registeredTeams: 12,
    totalTeams: 16,
    revenue: 12800,
    isArchived: false,
  },
  {
    id: 2,
    name: "Girls Volleyball Showcase",
    status: "registrationOpen",
    dateRange: "May 20-21, 2024",
    venue: "Overland Park, KS",
    registeredTeams: 8,
    totalTeams: 12,
    revenue: 8400,
    isArchived: false,
  },
  {
    id: 3,
    name: "Summer Showdown 2024",
    status: "draft",
    dateRange: "June 10-12, 2024",
    venue: "Topeka, KS",
    registeredTeams: 0,
    totalTeams: 20,
    revenue: 0,
    isArchived: false,
  },
  {
    id: 4,
    name: "Spring Classic 2024",
    status: "completed",
    dateRange: "April 1-3, 2024",
    venue: "St. Louis, MO",
    registeredTeams: 14,
    totalTeams: 14,
    revenue: 14700,
    isArchived: true,
  },
];

const statusOptions: { value: TournamentStatus; label: string }[] = [
  { value: "draft", label: "Draft" },
  { value: "published", label: "Published" },
  { value: "registrationOpen", label: "Registration Open" },
  { value: "inProgress", label: "In Progress" },
  { value: "completed", label: "Completed" },
];

function StatusBadge({ status }: { status: TournamentStatus }) {
  const styles: Record<string, string> = {
    draft: "bg-secondary text-foreground/60",
    published: "bg-success-light text-success",
    registrationOpen: "bg-info-light text-info",
    inProgress: "bg-warning-light text-warning",
    completed: "bg-muted text-muted-foreground",
  };
  const labels: Record<string, string> = {
    draft: "Draft",
    published: "Published",
    registrationOpen: "Registration Open",
    inProgress: "In Progress",
    completed: "Completed",
  };
  return (
    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${styles[status]}`}>
      {labels[status]}
    </span>
  );
}

export default function RLEventsTournaments() {
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState<"active" | "archived">("active");
  const [selectedStatuses, setSelectedStatuses] = useState<TournamentStatus[]>([]);

  const filteredTournaments = allTournaments.filter((t) => {
    const matchesTab = activeTab === "active" ? !t.isArchived : t.isArchived;
    const matchesSearch =
      t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.venue.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = selectedStatuses.length === 0 || selectedStatuses.includes(t.status);
    return matchesTab && matchesSearch && matchesStatus;
  });

  const toggleStatusFilter = (status: TournamentStatus) => {
    setSelectedStatuses((prev) =>
      prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status]
    );
  };

  return (
    <AppLayout userName="Sarah Johnson" userRole="Tournament Operator">
      <div className="min-h-screen bg-background">
        {/* Page header */}
        <div className="border-b border-border bg-background px-4 sm:px-6 lg:px-8 py-6">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <h1 className="text-3xl font-bold text-foreground">Events</h1>
              <div className="flex gap-3 flex-wrap">
                <button className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border text-foreground hover:bg-secondary transition-colors font-medium text-sm">
                  <Copy className="w-4 h-4" />
                  Clone from previous
                </button>
                <Link
                  to="/events/create"
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-white hover:opacity-90 transition-opacity font-medium text-sm"
                >
                  <Plus className="w-4 h-4" />
                  Create Event
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="border-b border-border bg-background px-4 sm:px-6 lg:px-8 py-4">
          <div className="max-w-7xl mx-auto space-y-4">
            {/* Search */}
            <div className="relative">
              <input
                type="text"
                placeholder="Search tournaments by name or venue..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full rounded-lg border border-border bg-card pl-10 pr-4 py-2 text-sm placeholder-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-colors"
              />
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            </div>

            {/* Status filters */}
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setSelectedStatuses([])}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  selectedStatuses.length === 0
                    ? "bg-primary text-white"
                    : "bg-secondary text-secondary-foreground hover:bg-muted"
                }`}
              >
                All
              </button>
              {statusOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => toggleStatusFilter(option.value)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    selectedStatuses.includes(option.value)
                      ? "bg-primary text-white"
                      : "bg-secondary text-secondary-foreground hover:bg-muted"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-border bg-background px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto flex gap-6">
            <button
              onClick={() => setActiveTab("active")}
              className={`py-4 px-1 text-sm font-medium border-b-2 transition-colors ${
                activeTab === "active"
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              Active
            </button>
            <button
              onClick={() => setActiveTab("archived")}
              className={`py-4 px-1 text-sm font-medium border-b-2 transition-colors ${
                activeTab === "archived"
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              Archived
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="px-4 sm:px-6 lg:px-8 py-8">
          <div className="max-w-7xl mx-auto">
            {filteredTournaments.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredTournaments.map((t) => (
                  <Link
                    key={t.id}
                    to={`/events/${t.id}`}
                    className="bg-white rounded-2xl p-5 shadow-sm hover:shadow-md transition-all cursor-pointer border border-border"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <h3 className="font-semibold text-foreground hover:text-primary transition-colors flex-1">
                        {t.name}
                      </h3>
                    </div>

                    <div className="mb-3">
                      <StatusBadge status={t.status} />
                    </div>

                    <div className="space-y-2 text-sm text-muted-foreground mb-4">
                      <div>{t.dateRange}</div>
                      <div>{t.venue}</div>
                    </div>

                    {/* Teams progress */}
                    <div className="pb-4 border-b border-border mb-4">
                      <div className="flex justify-between text-xs mb-2">
                        <span className="text-muted-foreground">Teams registered</span>
                        <span className="font-medium text-foreground">
                          {t.registeredTeams} of {t.totalTeams}
                        </span>
                      </div>
                      <div className="w-full bg-secondary rounded-full h-2">
                        <div
                          className="bg-primary h-2 rounded-full transition-all"
                          style={{
                            width: `${(t.registeredTeams / t.totalTeams) * 100}%`,
                          }}
                        />
                      </div>
                    </div>

                    {/* Revenue */}
                    <div>
                      <p className="text-xs text-muted-foreground">Revenue</p>
                      <p className="text-lg font-bold text-foreground">
                        ${t.revenue.toLocaleString()}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="rounded-lg border border-border bg-card p-12 text-center">
                <AlertCircle className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                <p className="text-foreground font-semibold mb-1">No tournaments found</p>
                <p className="text-muted-foreground text-sm mb-4">
                  Try adjusting your filters or create a new tournament.
                </p>
                <Link
                  to="/events/create"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-white hover:opacity-90 transition-opacity font-medium text-sm"
                >
                  <Plus className="w-4 h-4" />
                  Create Event
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
